import { getHubImageUrl, HubItemElementKind } from "client/Api/Hub/common";
import {
	HorizontalLineElementSaveModel,
	BlobIdImageElementComposerViewModel,
	ParagraphElementSaveModel,
	SubtitleElementSaveModel,
	VideoElementSaveModel,
	EmbedElementSaveModel
} from "client/Api/Hub/common.composer";
import { never } from "client_legacy/ndaw/errors";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useContext,
	useImperativeHandle,
	useRef
} from "react";
import { HorizontalLine } from "./HorizontalLine/HorizontalLine";
import { Image } from "./Image/Image";
import { Paragraph } from "./Paragraph";
import { Subtitle } from "./Subtitle/Subtitle";
import { Video } from "./Video/Video";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ensureNotNil,
	getDataUrl,
	isNil
} from "client/Misc/utils";
import { clearFocus } from "client/Misc/domUtils";
import { EmbedInputDialogData } from "../../HubMediaSelect/EmbedInputDialog";
import { Guid } from "client_legacy/ndaw/guid";
import { BuilderContext } from "../BuilderContext";
import { ObjectWithClientId, setClientId } from "client/Misc/clientId";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { Embed } from "./Embed/Embed";
import { VideoInputDialogData } from "../../HubMediaSelect/VideoInputDialog";
import { FrequentlyAskedQuestions } from "./FrequentlyAskedQuestions/FrequentlyAskedQuestions";
import {
	FrequentlyAskedQuestionElementFormModel,
	HubItemElementFormModel,
	ImageElementFormModel
} from "../../Management/common";
import { fileMetadataSymbol } from "client/Misc/commonTypes";
import { hubItemKindToHubDataSourceMap } from "../../Sidebar/common";
import { HttpClient } from "client/Api/httpClient";
import { ImageWithAttribution, HubImageSelectorKind } from "../../Management/useHubImageSelector";
import { convertToAttributionSaveModel } from "../../converters";

export type ElementProps = {
	hubItemKind: HubItemKind;
	layoutLocation: {
		row: number;
		column: number;
		element: number;
	};
	attachable: IValueInputAttachable<ObjectWithClientId<HubItemElementFormModel>>;
	originalImages: Map<number, BlobIdImageElementComposerViewModel>;
	onAddNewElementToColumn(element: HubItemElementFormModel): void;
	onDuplicate: () => void;
	onDelete: () => void;
	onOpenEmbedInputDialog(
		data: EmbedInputDialogData
	): Promise<TryResult<string>>;
	onOpenVideoInputDialog(
		data: VideoInputDialogData
	): Promise<TryResult<string>>;
	onMoveUp?(): void;
	onMoveDown?(): void;
	onSelectImage: (kind: HubImageSelectorKind) => Promise<TryResult<ImageWithAttribution>>;
};

export type ElementHandle = {
	findAndFocus(clientId: Guid): void;
	scrollIntoView(): void;
};

export const Element = forwardRef(
	(props: ElementProps, ref: Ref<ElementHandle>): ReactElement => {
		const {
			hubItemKind,
			layoutLocation,
			attachable,
			originalImages,
			onDuplicate,
			onAddNewElementToColumn,
			onDelete,
			onOpenEmbedInputDialog,
			onOpenVideoInputDialog,
			onMoveUp,
			onMoveDown,
			onSelectImage
		} = props;

		const paragraphRef: RefObject<ElementGenericHandle | null> = useRef<ElementGenericHandle>(null);
		const embedRef: RefObject<ElementGenericHandle | null> = useRef<ElementGenericHandle>(null);
		const subtitleRef: RefObject<ElementGenericHandle | null> = useRef<ElementGenericHandle>(null);
		const imageRef: RefObject<ElementGenericHandle | null> = useRef<ElementGenericHandle>(null);
		const faqRef: RefObject<ElementGenericHandle | null> = useRef<ElementGenericHandle>(null);
		const horizontalLineRef: RefObject<ElementGenericHandle | null> = useRef<ElementGenericHandle>(null);
		const videoRef: RefObject<ElementGenericHandle | null> = useRef<ElementGenericHandle>(null);

		const {
			imageRestrictions,
			isPixabayImageGalleryAvailable
		} = useContext(BuilderContext);

		const {
			maxImageSize
		} = imageRestrictions;

		const kind: HubItemElementKind = attachable.value
			.kind;

		const resolveGenericHandle = (kind: HubItemElementKind): ElementGenericHandle => {
			switch (kind) {
				case HubItemElementKind.Paragraph:
					return ensureNotNil(paragraphRef.current);
				case HubItemElementKind.Subtitle:
					return ensureNotNil(subtitleRef.current);
				case HubItemElementKind.Image:
					return ensureNotNil(imageRef.current);
				case HubItemElementKind.Video:
					return ensureNotNil(videoRef.current);
				case HubItemElementKind.Embed:
					return ensureNotNil(embedRef.current);
				case HubItemElementKind.HorizontalLine:
					return ensureNotNil(horizontalLineRef.current);
				case HubItemElementKind.FrequentlyAskedQuestion:
					return ensureNotNil(faqRef.current);
				default:
					never(kind);
			}
		};

		useImperativeHandle(
			ref,
			(): ElementHandle => ({
				findAndFocus(clientId: Guid): void {
					resolveGenericHandle(kind)
						.findAndFocus(clientId);
				},
				scrollIntoView(): void {
					resolveGenericHandle(kind)
						.scrollIntoView();
				}
			}),
			[kind]
		);

		const genericHandlers: ElementGenericActionHandlers = {
			onAddNewElementToColumn,
			onOpenEmbedInputDialog,
			onOpenVideoInputDialog,
			onSelectImage,
			onMoveUp,
			onMoveDown,
			onDuplicate: (): void => {
				clearFocus();
				// rich text editor only update the form handler on blur which means that
				// the onDuplicate will trigger before the forHandler value is updated
				setTimeout(onDuplicate, 0);
			},
			onDelete: (): void => {
				clearFocus();
				onDelete();
			}
		};

		switch (attachable.value.kind) {
			case HubItemElementKind.Paragraph:
				return (
					<Paragraph
						attachable={attachable as IValueInputAttachable<ParagraphElementSaveModel>}
						ref={paragraphRef}
						genericHandlers={genericHandlers}
						hubItemKind={hubItemKind}
					/>
				);
			case HubItemElementKind.Subtitle:
				return (
					<Subtitle
						attachable={attachable as IValueInputAttachable<SubtitleElementSaveModel>}
						ref={subtitleRef}
						genericHandlers={genericHandlers}
						hubItemKind={hubItemKind}
					/>
				);
			case HubItemElementKind.HorizontalLine:
				return (
					<HorizontalLine
						attachable={attachable as IValueInputAttachable<HorizontalLineElementSaveModel>}
						genericHandlers={genericHandlers}
						ref={horizontalLineRef}
					/>
				);
			case HubItemElementKind.Image:
				return (
					<Image
						hubItemKind={hubItemKind}
						attachable={attachable as IValueInputAttachable<ImageElementFormModel>}
						originalImage={attachable.value.id
							? getOriginalImage(originalImages, attachable.value.id)
							: null
						}
						genericHandlers={genericHandlers}
						isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
						maxImageSize={maxImageSize}
						ref={imageRef}
					/>
				);
			case HubItemElementKind.Embed:
				// Embed and video are done through an iframe, and when an iframe is moved in the DOM
				// it reinitializes. However with correct key usage of a collection mapping in react,
				// a react component is not remounted. Since we want to have the react component
				// remounted when the iframe reinitializes (i.e.is moved) to make sure the iframe
				// is configured correctly again, we use the key to force that
				return (
					<Embed
						key={JSON.stringify(layoutLocation)}
						attachable={attachable as IValueInputAttachable<EmbedElementSaveModel>}
						genericHandlers={genericHandlers}
						ref={embedRef}
					/>
				);
			case HubItemElementKind.Video:
				return (
					<Video
						key={JSON.stringify(layoutLocation)}
						attachable={attachable as IValueInputAttachable<VideoElementSaveModel>}
						genericHandlers={genericHandlers}
						ref={videoRef}
					/>
				);
			case HubItemElementKind.FrequentlyAskedQuestion:
				return (
					<FrequentlyAskedQuestions
						ref={faqRef}
						attachable={attachable as IValueInputAttachable<FrequentlyAskedQuestionElementFormModel>}
						onDeleteElement={onDelete}
						genericHandlers={genericHandlers}
						hubItemKind={hubItemKind}
					/>
				);
			default:
				never(attachable.value);
		}
	}
);
Element.displayName="Element";

export type ElementGenericActionHandlers = {
	onAddNewElementToColumn: (element: HubItemElementFormModel) => void;
	onOpenEmbedInputDialog: (data: EmbedInputDialogData) => Promise<TryResult<string>>;
	onOpenVideoInputDialog: (data: VideoInputDialogData) => Promise<TryResult<string>>;
	onSelectImage: (kind: HubImageSelectorKind) => Promise<TryResult<ImageWithAttribution>>;
	onMoveUp?: () => void;
	onMoveDown?: () => void;
	onDuplicate: () => void;
	onDelete: () => void;
};

export type ElementGenericHandle = {
	findAndFocus: (clientId: Guid) => void;
	scrollIntoView: () => void;
};

function getOriginalImage(
	originalImages: Map<number, BlobIdImageElementComposerViewModel>,
	elementId: number
): BlobIdImageElementComposerViewModel {
	const image: BlobIdImageElementComposerViewModel | undefined = originalImages.get(elementId);

	if (isNil(image)) {
		throw new Error(`The image element with id ${elementId} doesn't have an image data`);
	}

	return image;
}

export async function duplicateElement(
	element: ObjectWithClientId<HubItemElementFormModel>,
	originalImages: Map<number, BlobIdImageElementComposerViewModel>,
	hubItemKind: HubItemKind
): Promise<ObjectWithClientId<HubItemElementFormModel>> {
	if (element.kind !== HubItemElementKind.Image
		|| element.image.isSet
	) {
		return setClientId({
			...element,
			id: null
		});
	}

	const originalImage: BlobIdImageElementComposerViewModel = getOriginalImage(
		originalImages,
		ensureNotNil(element.id)
	);

	const {
		name,
		blobId,
		attribution
	} = originalImage;

	const imageUrl: string = getHubImageUrl(
		hubItemKindToHubDataSourceMap[hubItemKind],
		blobId
	);

	try {
		const client = new HttpClient();

		const imageBlob: Blob = await client.getBlob(
			imageUrl,
			null,
			{ cache: true }
		);

		if (!imageBlob.type.includes("image")) {
			throw new Error(`Response content type is not an image for "${name}"`);
		}

		const uri: string = await getDataUrl(imageBlob);

		return setClientId({
			...element,
			id: null,
			kind: HubItemElementKind.Image,
			image: {
				isSet: true,
				value: {
					filename: name,
					uri,
					[fileMetadataSymbol]: {
						mimeType: imageBlob.type,
						size: imageBlob.size
					},
					attribution: attribution
						? convertToAttributionSaveModel(attribution)
						: null
				}
			}
		});
	} catch (e) {
		console.error("Could not load the blob from the backend", e);
		throw e;
	}
}
